import _ from 'lodash';
import {
  getAllChildItems,
  getSameLevelItems,
  getMainMenuOption,
} from '../../services/product';
import {PAGE, PRODUCT, PRODUCT_GROUP, SUB_MENU} from '../../constants/MenuItemTypes';
import axios from "axios";
import {SET_CATERING_MINIMUM_SPEND} from "../quote";
import moment from "moment";


export function getIncludedGuests(guests, exclusions) {

  const includedGuests = _.filter(guests, (g) => {
    return !exclusions.includes(g.id);
  });

  return _.sumBy(Object.values(includedGuests), 'total');
}

const replaceOtherMainMenus = (item, menu) => {
  const option = getMainMenuOption(item, menu);
  if (!option) return menu;
  getSameLevelItems(option, menu).forEach(sibling => {
    if (sibling.id !== option.id && getMainMenuOption(sibling, menu) === sibling) {
      // Clear groups as well as products: retained selected group flags can
      // otherwise revive dishes during the next quantity recomputation.
      getAllChildItems(sibling, menu).forEach(child => {
        menu[child.id] = { ...menu[child.id], selected: false, quantity: 0, total: 0, manually_set_quantity: false };
      });
    }
  });
  return menu;
};

const maximumProductChoices = (item, menu, rules) => {
  let currentChoices = getSameLevelItems(item, menu, { selected: true });
  if(rules.maximum_choices === 1) {
    _.each(currentChoices, (i) => {
    if(i.id !== item.id) {
        menu[i.id].selected = false;
      }
    });
    return menu;
  }
  if ((currentChoices.length >= rules.maximum_choices) && currentChoices.length !== 0) {
    _.each(getSameLevelItems(item, menu, { selected: false }), (i) => {
      if(i.id !== item.id) {
        menu[i.id].is_disabled.push('maximum group limit');
      }
    });
  }
  else {
    _.each(_.filter(getSameLevelItems(item, menu), (e) => (e.is_disabled.length > 0)), (i) => {
      menu[i.id].is_disabled = _.pull(menu[i.id].is_disabled, 'maximum group limit');
    });
  }
  return menu;
}

// maximum_quantity=0 is stored to mean "no maximum" across ~132 production
// menu items (the admin UI renders 0 as an empty field) — the engine must
// never treat it as a literal hard cap of zero. minimum_quantity is
// unaffected: 0 there is a real, literal floor.
export const hasMaximumQuantity = (item) => (
  item.maximum_quantity !== null && item.maximum_quantity !== 0
);

// Ivy Lodge's evening-food menu has four style groups. The limit on each
// group applies to varieties within that style; it does not stop a customer
// choosing varieties from a second style. Recognise the section by its menu
// shape so the rule also works when a new seasonal menu gets different IDs.
const eveningFoodStyleLimits = { Sliders: 3, Tacos: 3, Pizza: 3, Bowls: 2 };

const clearOtherEveningFoodStyles = (item, menu) => {
  const style = item.menu_item_type_id === PRODUCT_GROUP ? item : menu[item.parent_id];
  if (!style || style.menu_item_type_id !== PRODUCT_GROUP) return menu;

  const section = menu[style.parent_id];
  if (!section || section.menu_item_type_id !== SUB_MENU || section.label !== 'Evening food') return menu;

  const styles = getSameLevelItems(style, menu);
  if (styles.length !== 4 || !styles.every((candidate) => (
    candidate.menu_item_type_id === PRODUCT_GROUP
    && candidate.maximum_choices === eveningFoodStyleLimits[candidate.label]
  ))) return menu;

  _.forEach(styles, (candidate) => {
    if (candidate.id !== style.id
      && (candidate.selected || selectedChildProducts(candidate, menu).length > 0)) {
      menu = setItemUnSelected(candidate, menu);
    }
  });
  return menu;
};

export const setItemSelected = (item, menu, guests, rules, distributeCount, addRemainder) => {
  menu = clearOtherEveningFoodStyles(item, menu);
  distributeCount = distributeCount || 1;
  addRemainder = addRemainder || false;
  let includedGuests = getIncludedGuests(guests, rules.exclusions);
  let quantity = Math.floor( includedGuests / distributeCount);
  if (addRemainder) {
    quantity += includedGuests % distributeCount;
  }
  quantity = item.per_person ? Math.floor(_.multiply(quantity, rules.guest_multiplier)) : quantity;

  // The marker is authoritative on its own — no `&& item.quantity > 0` guard. A
  // deliberate manual 0 is a real choice, and excluding it meant the engine
  // recomputed over the top of it and then pinned the item at the recomputed
  // value, quoting a quantity nobody asked for (CR-186 review). Deselecting
  // clears the marker in setItemUnSelected, so a 0 here is always the
  // customer's, never a leftover.
  if (item.manually_set_quantity === true) {
    quantity = item.quantity;
  }

  // minimum_quantity means two different things here, and the split is narrow on
  // purpose:
  //
  //   - a MANUAL quantity -> FLOOR (Math.max). As an override it discarded the
  //     customer's entry outright on any item carrying a minimum_quantity, which
  //     is why a typed pizza total snapped back to the venue's minimum (CR-190):
  //     menus1.json 17474 (floor 80, £15) quoted a manual 120 as £1,200 instead
  //     of £1,800. A manual value BELOW the floor is still raised to it, so the
  //     customer still cannot go under the venue's minimum.
  //   - an ENGINE-COMPUTED quantity -> absolute override, unchanged.
  //
  // Making the engine path a floor too would reprice live quotes (17474 goes
  // £1,200 -> £1,500, ~25%) and needs venue-config sign-off that no venue uses
  // minimum_quantity as a fixed pin. That is tracked separately in CR-194 and is
  // deliberately NOT done here, so this ticket changes no quote the customer did
  // not type themselves.
  if (menu[item.id].minimum_quantity !== null) {
    quantity = (item.manually_set_quantity === true)
        ? Math.max(menu[item.id].minimum_quantity, quantity)
        : menu[item.id].minimum_quantity;
  }

  quantity = (hasMaximumQuantity(menu[item.id]) && menu[item.id].maximum_quantity < quantity)
      ? menu[item.id].maximum_quantity
      : quantity;

  menu[item.id].quantity = quantity;
  menu[item.id].selected = true;
  menu[item.id].total = _.multiply(item.unit_price, quantity);

  if (item.menu_item_type_id === PRODUCT && menu[item.parent_id] && menu[item.parent_id].menu_item_type_id === PRODUCT_GROUP) {
    menu[item.parent_id].selected = true;
  }

  if (item.menu_item_type_id !== PRODUCT_GROUP) {
    let childProducts = _.filter(getAllChildItems(item, menu), (menuItem) => {
      return menuItem.id !== item.id && menuItem.menu_item_type_id === PRODUCT;
    })

    _.forEach(childProducts, (childProduct) => {
      if (childProduct.is_compulsory === 1) {
        menu = setItemSelected(childProduct, menu, guests, getRules(childProduct, menu));
      }
      menu = distributeQuantities(childProduct, menu, guests);
    });
  }

  return menu;
}

export const selectedChildProducts = (item, menu) => {
  return _.filter(getAllChildItems(item, menu), (menuItem) => {
    return menuItem.id !== item.id && menuItem.menu_item_type_id === PRODUCT && menuItem.selected === true;
  })
}

export const setItemUnSelected = (item, menu) => {
  menu[item.id].quantity = 0;
  menu[item.id].total = 0;
  menu[item.id].selected = false;
  menu[item.id].manually_set_quantity = false;

  let childProducts = _.filter(getAllChildItems(item, menu), (menuItem) => {
    return menuItem.id !== item.id && menuItem.menu_item_type_id === PRODUCT;
  })

  _.forEach(childProducts, (childProduct) => {
    menu = setItemUnSelected(menu[childProduct.id], menu);
  });

  return menu;
}

export const getRules = (item, menu, rules) => {
  rules = rules || {
    guest_multiplier: 1,
    exclusions: [],
    distribute_ids: [],
    distribute_selected: [],
  };

  if (item.parent_id) {
    rules = getRules(menu[item.parent_id], menu, rules);
  }

  if (item.distribute_quantity === 1) {
    const children = _.filter(getAllChildItems(item, menu), (menuItem) => {
      return menuItem.menu_item_type_id === PRODUCT;
    });

    rules.distribute_ids = [];
    _.forEach(children, (child) => {
      rules.distribute_ids.push(child.id);

      if (child.selected === true) {
        rules.distribute_selected.push(child.id);
      }
    });
  }

  if (item.exclusions.length > 0) {
    rules.exclusions = rules.exclusions.concat(item.exclusions);
  }

  if (item.guest_multiplier !== null) {
    rules.guest_multiplier = item.guest_multiplier;
  }

  if (item.distribute_quantity > 0) {
    rules.distribute_quantity_from_id = item.id;
  }

  return rules;
}

export const distributeQuantities = (item, menu, guests) => {
  const rules = getRules(item, menu);

  if (rules.distribute_ids.includes(item.id)) {
    _.forEach(rules.distribute_ids, (id, index) => {
      if (menu[id].selected === true) {
        menu = setItemSelected(menu[id], menu, guests, rules, rules.distribute_selected.length, index === 0);
      } else {
        menu = setItemUnSelected(menu[id], menu);
      }
    });
  }

  return menu;
}

// getDistributeGroup / getDistributeGroupTotal / getDistributeGroupFloor /
// clampManualQuantity / clearDistributeManualQuantities support the quantity
// picker (components/QuantityPicker.jsx) and the SET_QUANTITY reducer case
// (redux/quote.js). They are the single source of truth for "how far can a
// manual edit push a distribute-group member" — see the quote-pricing skill
// (.claude/skills/quote-pricing/SKILL.md) for the full design.

// Thin wrapper over getRules: null unless `item` is one of the products a
// distribute_quantity ancestor shares the guest count across. selectedIds is
// derived from each member's own `selected` flag — NOT `rules.distribute_selected`,
// which double-counts across nested distribute ancestors.
export const getDistributeGroup = (item, menu) => {
  const rules = getRules(item, menu);

  if (!rules.distribute_ids.includes(item.id)) {
    return null;
  }

  const ids = rules.distribute_ids;
  const selectedIds = ids.filter((id) => menu[id].selected === true);

  return { ids, selectedIds, rules };
};

// Sum of quantity across the group's currently-selected members, letting the
// caller substitute a hypothetical quantity for any id via `overrides`
// without cloning the menu. 0 for a non-distribute item.
export const getDistributeGroupTotal = (item, menu, overrides = {}) => {
  const group = getDistributeGroup(item, menu);

  if (group === null) {
    return 0;
  }

  return _.sumBy(group.selectedIds, (id) => (
    overrides[id] !== undefined ? overrides[id] : menu[id].quantity
  ));
};

// Shared by getDistributeGroupFloor and getDistributeGroupNaturalQuantity:
// re-runs the engine's own distribution for this exact selection, with every
// group member's manually_set_quantity marker stripped first — cleared on ALL
// members, not just the edited one, so neither caller can ratchet upward
// permanently as users raise siblings. Computed on a deep clone; the menu
// passed in is never mutated. null for a non-distribute item.
const recomputeDistributeGroup = (item, menu, guests) => {
  const group = getDistributeGroup(item, menu);

  if (group === null) {
    return null;
  }

  const clone = _.cloneDeep(menu);
  _.forEach(group.ids, (id) => {
    clone[id].manually_set_quantity = false;
  });

  return { group, distributed: distributeQuantities(clone[item.id], clone, guests) };
};

// The floor is whatever the engine itself would produce for this exact
// selection today (see recomputeDistributeGroup). 0 for a non-distribute item.
export const getDistributeGroupFloor = (item, menu, guests) => {
  const result = recomputeDistributeGroup(item, menu, guests);

  if (result === null) {
    return 0;
  }

  const { group, distributed } = result;

  return _.sumBy(group.selectedIds, (id) => distributed[id].quantity);
};

// This item's own share of the engine's own distribution for the current
// selection (see recomputeDistributeGroup) — i.e. what the engine would give
// this item right now if nobody had manually overridden anything in the
// group. Falls back to the item's own current quantity for a non-distribute
// item, since there is no group redistribution to compare against.
//
// Used by SET_QUANTITY (redux/quote.js) to tell a genuine manual override
// apart from an edit that merely landed back on the value the engine would
// have assigned anyway — comparing against the item's PREVIOUS quantity
// instead pinned an item that had only been clamped back to its natural
// share, desyncing it from its distribute-group siblings (CR-186 review).
export const getDistributeGroupNaturalQuantity = (item, menu, guests) => {
  const result = recomputeDistributeGroup(item, menu, guests);

  if (result === null) {
    return item.quantity;
  }

  return result.distributed[item.id].quantity;
};

// Clamps a picker-entered quantity in a fixed order: coerce -> minimum_quantity
// floor -> distribute-group floor -> maximum_quantity ceiling (always applied
// last, so it wins even over the group floor). This is a picker/reducer-only
// concern, separate from rules.js:60-62's own minimum_quantity handling in the
// engine's compute path, which is untouched.
export const clampManualQuantity = (item, menu, guests, requested) => {
  let value = parseInt(requested, 10);
  if (Number.isNaN(value) || value < 0) {
    value = 0;
  }

  if (item.minimum_quantity !== null) {
    value = Math.max(value, item.minimum_quantity);
  }

  const group = getDistributeGroup(item, menu);
  if (group !== null) {
    const floor = getDistributeGroupFloor(item, menu, guests);
    const othersTotal = getDistributeGroupTotal(item, menu, { [item.id]: 0 });
    const need = floor - othersTotal;
    if (value < need) {
      value = need;
    }
  }

  if (hasMaximumQuantity(item)) {
    value = Math.min(value, item.maximum_quantity);
  }

  return value;
};

// Mutates and returns `menu` (matching setItemSelected/distributeQuantities'
// existing house style). Scoped to distribute-group members only — a manual
// quantity on a non-distribute picker item (e.g. extra canapés) must survive a
// guest-count change, and clearing it here would break that.
export const clearDistributeManualQuantities = (menu) => {
  _.forEach(menu, (item) => {
    if (item.manually_set_quantity && getDistributeGroup(item, menu) !== null) {
      menu[item.id].manually_set_quantity = false;
    }
  });

  return menu;
};

const checkMaximumChoices = (item, menu) => {
  const parent = menu[item.parent_id] || null;
  let maximumChoices = parent && parent.maximum_choices ? parent.maximum_choices : null;

  if (maximumChoices === null) {
    return menu;
  }

  // `siblings` INCLUDES the item being processed, and must: the
  // `maximum_choices > 1` check below counts selections against the venue's cap,
  // so dropping `item` from this list would silently allow one extra selection
  // (CR-190 review — verified: a cap of 2 accepted 3).
  const siblings = getAllChildItems(parent, menu).filter((i) => {
    return i.id !== parent.id && i.depth === parent.depth + 1;
  });

  if (maximumChoices === 1) {
    // Unselect the OTHER options only. A radio group never unselects the one
    // being selected, and doing so here was destructive rather than cosmetic:
    // setItemUnSelected zeroes quantity and total and clears
    // manually_set_quantity, and updateMenu calls setItemSelected immediately
    // afterwards — so the item came back selected with the customer's typed
    // quantity replaced by the guest-derived default. That is the "resets to the
    // total number of guests" report in CR-190, and it fired on every recompute
    // for any picker item under such a parent (menus1.json 17474 is the only one
    // in the fixtures, and it is the sole member of its group, so the loop was
    // unselecting nothing else at all).
    _.forEach(siblings, (sibling) => {
      if (sibling.id !== item.id) {
        menu = setItemUnSelected(sibling, menu);
      }
    });

    return menu;
  }

  const selectedSiblings = siblings.filter((sibling) => {
    return sibling.selected === true;
  });

  if (selectedSiblings.length > maximumChoices) {
    const error = new Error('Only ' + maximumChoices + ' are allowed here');
    error.item_id = parent.id;
    throw error;
  }

  return menu;
}

export function updateCateringMinimumSpend() {
  return function (dispatch, getState) {

    const config = getState().config;
    const quote = getState().quote;
    // A date change commits before its hire price. Do not send an incomplete
    // total to the minimum-spend endpoint or display the previous date's reply.
    if (['loading', 'error'].includes(quote.selectedDate.priceStatus)) return;
    const date = quote.selectedDate.date || moment();
    const priceRequestId = quote.datePriceRequestId;
    const selected = _.filter(quote.menu, 'selected');


    const mapped = selected.map((i) => {
      if(i.total > 0) {
        return {
          product_id: i.product_id,
          total: i.total,
        }
      }

    });
    mapped.push({
      product_id: 12098,
      total: quote.selectedDate.price,
    });



    axios
        .post(`${config.appUrl}/api/location/cateringMinimumSpend?location_id=${config.app_id}&hire_from=${date.format('DD-MM-YYYY')}`,
            {selected:mapped},
            {
              headers: {
                Accept: 'application/json',
              },
            })
        .then((response) => {
          const current = getState().quote;
          if (current.datePriceRequestId !== priceRequestId
            || (current.selectedDate.date && !current.selectedDate.date.isSame(date, 'day'))) return;
          dispatch({
            type: SET_CATERING_MINIMUM_SPEND,
            payload: response.data,
          });
        });
  }
}


export const updateMenu = (item, menu, guests) => {
  if (item.menu_item_type_id === PAGE) {
    return menu;
  }

  let rules = getRules(item, menu);

  if (item.selected || selectedChildProducts(item, menu).length > 0) {
    try {
      menu = replaceOtherMainMenus(item, menu);
      menu = checkMaximumChoices(item, menu);
      menu = setItemSelected(item, menu, guests, rules);
    } catch (e) {
      menu = setItemUnSelected(item, menu);
    }
  } else {
    menu = setItemUnSelected(item, menu);
  }

  menu = distributeQuantities(item, menu, guests);

  return menu;
}

export const chooseMenu = (item, menu, guests) => {
  let rules = getRules(item, menu);


    try {
      menu = replaceOtherMainMenus(item, menu);
      menu = checkMaximumChoices(item, menu);
      menu = setItemSelected(item, menu, guests, rules);
    } catch (e) {
      menu = setItemUnSelected(item, menu);
    }


  menu = distributeQuantities(item, menu, guests);
  return menu;
}

export function toggleProduct(item) {

  return function (dispatch, getState) {
    let menu = getState().quote.menu;

    menu[item.id].selected = !item.selected;
    menu = updateMenu(item, menu, getState().quote.guests)

    dispatch({type: 'UPDATE_CHOICES', payload: { menu }});
    dispatch(updateCateringMinimumSpend());
  }
}

export function getMenuId(menu) {
  const keys = menu ? Object.keys(menu) : [];
  if (keys.length === 0) return null;
  return menu[keys[0]].menu_id || null;
}
